export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_REQUIREMENTS_MESSAGE =
  `Use at least ${PASSWORD_MIN_LENGTH} characters. Common or breached passwords are not allowed.`;

export const PASSWORD_MANAGER_MESSAGE =
  "A password manager can create and save a strong, unique password for you.";

export const PASSWORD_ERROR_CODES = Object.freeze({
  tooShort: "password_too_short",
  missingCharacterClasses: "password_missing_character_classes",
  compromised: "compromised_password",
  mismatch: "password_mismatch",
  reused: "password_reused",
  generic: "password_update_failed",
});

export const ADMIN_PASSWORD_SETUP_ERROR_CODES = Object.freeze({
  sessionMissing: "admin_session_missing",
  invitationExpired: "admin_invitation_expired",
  accessDenied: "admin_access_denied",
  rateLimited: "admin_password_rate_limited",
  reauthenticationNeeded: "admin_reauthentication_needed",
  setupIncomplete: "admin_password_setup_incomplete",
  responseUnclear: "admin_password_response_unclear",
  unexpected: "admin_password_unexpected",
});

const ADMIN_PASSWORD_SETUP_MESSAGES = Object.freeze({
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.sessionMissing]:
    "Your administrator session is missing. Open a new invitation link and try again.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.invitationExpired]:
    "This administrator invitation is no longer valid. Request a new invitation and try again.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.accessDenied]:
    "This account does not have administrator access. Ask an administrator to send a new invitation.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.rateLimited]:
    "Too many password attempts. Wait a moment before trying again.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.reauthenticationNeeded]:
    "Your administrator session needs to be verified again. Request a new invitation and try again.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.setupIncomplete]:
    "Your password was saved, but administrator setup did not finish. Please contact support.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.responseUnclear]:
    "We couldn't confirm whether your password was saved. Refresh this page before trying again.",
  [ADMIN_PASSWORD_SETUP_ERROR_CODES.unexpected]:
    "We couldn't create your password right now. Please try again.",
});

export const COMPROMISED_PASSWORD_TITLE = "Choose a different password";
export const COMPROMISED_PASSWORD_MESSAGE =
  "This password is commonly known or has appeared in previous data breaches. Try a more unique password or use one generated by your password manager.";

const PASSWORD_MESSAGES = Object.freeze({
  [PASSWORD_ERROR_CODES.tooShort]: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
  [PASSWORD_ERROR_CODES.missingCharacterClasses]:
    "This password is missing one or more required character types.",
  [PASSWORD_ERROR_CODES.compromised]:
    `${COMPROMISED_PASSWORD_TITLE}. ${COMPROMISED_PASSWORD_MESSAGE}`,
  [PASSWORD_ERROR_CODES.mismatch]: "Passwords must match.",
  [PASSWORD_ERROR_CODES.reused]: "Choose a password you have not used before.",
});

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function getReasons(error) {
  return Array.isArray(error?.reasons) ? error.reasons.map(normalize) : [];
}

export function getPasswordValidationErrorCode(password, confirmation) {
  if (typeof password !== "string" || password.length < PASSWORD_MIN_LENGTH) {
    return PASSWORD_ERROR_CODES.tooShort;
  }
  if (confirmation !== undefined && password !== confirmation) {
    return PASSWORD_ERROR_CODES.mismatch;
  }
  return null;
}

export function getPasswordErrorCode(error) {
  const suppliedCode = normalize(error?.error || error?.code);
  if (Object.values(PASSWORD_ERROR_CODES).includes(suppliedCode)) {
    return suppliedCode;
  }
  if (suppliedCode === "invalid_payload") {
    return PASSWORD_ERROR_CODES.generic;
  }

  const name = normalize(error?.name);
  const message = normalize(error?.message || error);
  const reasons = getReasons(error);

  if (message.includes("passwords must match")) {
    return PASSWORD_ERROR_CODES.mismatch;
  }
  if (
    reasons.some((reason) => reason === "length" || reason.includes("length")) ||
    (message.includes("at least") && message.includes("character")) ||
    message.includes("too short")
  ) {
    return PASSWORD_ERROR_CODES.tooShort;
  }
  if (
    reasons.some((reason) =>
      ["characters", "uppercase", "lowercase", "digits", "numbers", "symbols"].some(
        (requiredClass) => reason.includes(requiredClass)
      )
    ) ||
    (message.includes("contain") &&
      ["uppercase", "lowercase", "number", "digit", "symbol"].some((term) =>
        message.includes(term)
      ))
  ) {
    return PASSWORD_ERROR_CODES.missingCharacterClasses;
  }
  if (
    reasons.includes("pwned") ||
    ((suppliedCode === "weak_password" || name === "authweakpassworderror") &&
      ["breach", "comprom", "pwned", "known to be weak", "easy to guess", "commonly known"].some(
        (term) => message.includes(term)
      ))
  ) {
    return PASSWORD_ERROR_CODES.compromised;
  }
  if (
    suppliedCode === "same_password" ||
    ["reuse", "previously used", "old password", "different from the old"].some((term) =>
      message.includes(term)
    )
  ) {
    return PASSWORD_ERROR_CODES.reused;
  }
  return PASSWORD_ERROR_CODES.generic;
}

export function isCompromisedPasswordError(error) {
  return getPasswordErrorCode(error) === PASSWORD_ERROR_CODES.compromised;
}

export function getPasswordSubmissionErrorMessage(error, fallbackMessage) {
  const code = getPasswordErrorCode(error);
  return (
    PASSWORD_MESSAGES[code] ||
    fallbackMessage ||
    "We couldn't update your password. Please try again."
  );
}

export function getPasswordErrorMessage(code, fallbackMessage) {
  return getPasswordSubmissionErrorMessage({ error: code }, fallbackMessage);
}

export function getAdminPasswordSetupErrorCode(error) {
  const code = normalize(error?.error || error?.code);
  const name = normalize(error?.name);
  const status = Number(error?.status || 0);

  if (Object.values(ADMIN_PASSWORD_SETUP_ERROR_CODES).includes(code)) return code;
  if (code === "profile_update_failed") {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.setupIncomplete;
  }
  if (
    ["session_not_found", "no_authorization"].includes(code) ||
    name === "authsessionmissingerror"
  ) {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.sessionMissing;
  }
  if (
    [
      "session_expired",
      "refresh_token_not_found",
      "refresh_token_already_used",
      "bad_jwt",
      "invalid_jwt",
      "invite_not_found",
      "otp_expired",
      "flow_state_not_found",
      "flow_state_expired",
    ].includes(code) ||
    name === "authinvalidjwterror"
  ) {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.invitationExpired;
  }
  if (code === "reauthentication_needed" || code === "reauthentication_not_valid") {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.reauthenticationNeeded;
  }
  if (code === "over_request_rate_limit" || status === 429) {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.rateLimited;
  }
  const passwordCode = getPasswordErrorCode(error);
  if (passwordCode !== PASSWORD_ERROR_CODES.generic) return passwordCode;
  if (code === "forbidden" || status === 403) {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.accessDenied;
  }
  if (code === "unauthorized" || status === 401) {
    return ADMIN_PASSWORD_SETUP_ERROR_CODES.sessionMissing;
  }

  return ADMIN_PASSWORD_SETUP_ERROR_CODES.unexpected;
}

export function getAdminPasswordSetupErrorMessage(error) {
  const code = getAdminPasswordSetupErrorCode(error);
  return ADMIN_PASSWORD_SETUP_MESSAGES[code] || getPasswordErrorMessage(code);
}
